import React, { ReactNode, useState } from "react";

import styles from "./alert.module.scss";

import AlertIcon from "./AlertIcon";
import AlertSimplified from "../dtos/AlertSimplified";
import AlertProps from "./AlertProps";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertType } from "../enums/alertType";

const Alert: React.FC<AlertProps> = (props) => {
  const {
    alertObj,
    children,
    closable,
    showIcon,
    truncate,
    titleView,
    className,
    messageClassName,
    titleClasName,
    showIconOnMobile,
    onClickLinkButton = () => {},
  } = props;

  // translations
  const tLabels = useTranslation("shared\\components").t;

  // state
  const [showAlert, setShowAlert] = useState(true);

  // helpers
  const getCustomAlertClass = (alertType: AlertType) => { 
    switch (alertType) {
      case AlertType.Warning:
        return styles.alertWarning;   
      case AlertType.Info:
        return styles.alertInfo;  
      case AlertType.Success:
        return styles.alertSuccess;  
      case AlertType.Danger:
        return styles.alertDanger;  
      default:
        return "";
    }
  }

  const alertClasses = (
    alertObj: AlertSimplified,
    closable?: boolean,
    className?: string
  ): string => {
    let alert = `alert ${styles.notificationAlert} ${getCustomAlertClass(alertObj.alertType!)}`;
    let cloasable = closable ? "alert-dismissible fade show" : "";

    return `${alert} ${cloasable} ${className !== undefined ? className : ""}`;
  };

  const textElementClasses = (truncate?: boolean, titleView?: boolean, customClass?: string) => {
    if (truncate || titleView || customClass) {
      let truncateClass = truncate ? " text-truncate" : "";
      let titleViewClass = titleView ? " d-block" : "";
      let customClassName = customClass ? ` ${customClass}` : "";

      return `${truncateClass}${titleViewClass}${customClassName}`;
    }

    return "";
  };

  const handleCloseOnClick = () => {
    setShowAlert(false);

    if (props.onClose) {
      props.onClose(alertObj);
    }
  };

  const handleOnClickLinkButton = () => { 
    onClickLinkButton(alertObj.alertParam);     
  };

  // sub-renders
  const renderAlertContent = (): ReactNode => {
    if (children) {
      return children;
    } else {
      return (
        <div className={`d-flex align-items-center flex-wrap ${truncate ? "text-truncate" : ""}`}>
          <strong className={`mr-1${textElementClasses(truncate, titleView, titleClasName)}`}>
            {alertObj.title}
          </strong>
          <span className={`mr-2${textElementClasses(truncate, titleView, messageClassName)}`}>
            {alertObj.message}
          </span>
          {alertObj.alertParam && alertObj.alertParam.route && !truncate && (
            <Link
              className={`btn btn-sm btn-${alertObj.alertType?.toLocaleLowerCase()}`}
              to={`/${alertObj.alertParam?.route}`}
              onClick={handleOnClickLinkButton}
            >
              {tLabels("alert.show")}
            </Link>
          )}
        </div>
      );
    }
  };

  // render
  if (showAlert) {
    return (
      <div className={alertClasses(alertObj, closable, className)} role="alert">
        <div className="row no-gutters align-items-center">
          {showIcon && (
            <div
              className={`col-auto pr-3${
                !showIconOnMobile ? " d-none d-sm-block" : ""
              }`}
            >
              <strong>
                <AlertIcon type={alertObj.alertType!} />
              </strong>
            </div>
          )}
          <div className="col">{renderAlertContent()}</div>
        </div>
        {closable && (
          <button
            type="button"
            className="close"
            data-dismiss="alert"
            aria-label="Close"
            onClick={handleCloseOnClick}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        )}
      </div>
    );
  } else return null;
};

Alert.defaultProps = {
  closable: false,
  showIcon: true,
  showIconOnMobile: true,
  truncate: false,
  titleView: false,
};

export default Alert;
